import React, { useEffect, useState, useRef } from 'react';

export default function LoadingOverlay({ message = 'Preparing your experience' }) {
  const [count, setCount] = useState(0);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);
  const showDelay = 120; // ms - wait briefly before showing overlay

  useEffect(() => {
    const handler = (e) => {
      try {
        const d = e && e.detail;
        if (!d) return;
        if (d.type === 'start') {
          setCount(c => c + 1);
        } else if (d.type === 'end') {
          setCount(c => Math.max(0, c - 1));
        } else if (typeof d === 'boolean') {
          setCount(d ? 1 : 0);
        }
      } catch (err) {
        // ignore
      }
    };

    window.addEventListener('app-loading', handler);
    return () => window.removeEventListener('app-loading', handler);
  }, []);

  useEffect(() => {
    // Show overlay only after a short delay to avoid flashes for quick requests
    if (count > 0) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setVisible(true);
        timerRef.current = null;
      }, showDelay);
    } else {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setVisible(false);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [count]);

  if (!visible) return null;

  return (
    <div style={styles.backdrop} role="status" aria-live="polite">
      <div style={styles.card}>
        <div style={styles.brand} aria-hidden>
          <img src="/static/img/WhitehatsLogo2.webp" alt="i-ConsentO logo" style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 8 }} />
        </div>

        <div style={styles.content}>
          <div style={styles.title}>ConsentData</div>
          <div style={styles.message}>{message}</div>
          <div style={styles.dots} aria-hidden>
            <span style={{ ...styles.dot, animationDelay: '0s' }} />
            <span style={{ ...styles.dot, animationDelay: '0.12s' }} />
            <span style={{ ...styles.dot, animationDelay: '0.24s' }} />
          </div>
        </div>

        <div style={styles.ring} aria-hidden>
          <svg viewBox="0 0 50 50" style={{ width: 54, height: 54 }}>
            <circle cx="25" cy="25" r="20" fill="none" stroke="rgba(15, 23, 42, 0.06)" strokeWidth="6" />
            <path d="M25 5 a20 20 0 0 1 0 40" stroke="url(#grad2)" strokeWidth="6" strokeLinecap="round" fill="none">
              <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1.2s" repeatCount="indefinite" />
            </path>
            <defs>
              <linearGradient id="grad2" x1="0" x2="1">
                <stop offset="0%" stopColor="#06b6d4" />
                <stop offset="100%" stopColor="#4f46e5" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <style>{animations}</style>
      </div>
    </div>
  );
}

const animations = `
@keyframes pulseDot {
  0% { transform: translateY(0); opacity: 0.6 }
  50% { transform: translateY(-6px); opacity: 1 }
  100% { transform: translateY(0); opacity: 0.6 }
}
`;

const styles = {
  backdrop: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(180deg, rgba(2,6,23,0.24), rgba(2,6,23,0.36))',
    backdropFilter: 'blur(4px)', zIndex: 9999,
    padding: 20,
  },
  card: {
    display: 'flex', alignItems: 'center', gap: 18,
    background: 'rgba(255,255,255,0.98)', borderRadius: 14, padding: '18px 22px',
    boxShadow: '0 12px 40px rgba(2,6,23,0.2)', minWidth: 360, maxWidth: '90%'
  },
  brand: {
    flex: '0 0 auto'
  },
  content: {
    flex: '1 1 auto', display: 'flex', flexDirection: 'column'
  },
  title: { fontSize: 16, fontWeight: 700, color: '#0f172a' },
  message: { marginTop: 4, fontSize: 13, color: '#475569' },
  dots: { marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' },
  dot: { width: 10, height: 10, background: 'linear-gradient(90deg,#06b6d4,#4f46e5)', borderRadius: '50%', display: 'inline-block', animation: 'pulseDot 0.9s infinite ease-in-out' },
  ring: { flex: '0 0 auto' }
};

